/* eslint-disable react/prop-types */
// Geführte Tour: Spotlight auf echte UI-Elemente, Schritt-für-Schritt.
// Startet automatisch beim ersten Login (pro Gerät), jederzeit neu startbar
// über Profil-Menü → „Tour starten" (Event "eg:start-tour").
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

const DONE_KEY = "eg_tour_done_v1";

const STEPS = [
  {
    center: true,
    title: "Willkommen bei Easy Gutachter! 👋",
    text: "In einer Minute zeigen wir dir die wichtigsten Funktionen — von der ersten Akte bis zur Abrechnung. Du kannst die Tour jederzeit überspringen und später über das Profil-Menü neu starten.",
  },
  {
    target: '[data-testid="dashboard-new-case-btn"]',
    title: "Neue Akte anlegen",
    text: "Hier startet jedes Gutachten: Klick auf „Neue Akte“ — das Aktenzeichen wird automatisch vergeben, danach führen dich die Schritte durch Kunde, Fahrzeug, Fotos und Gutachten.",
  },
  {
    target: '[data-testid="dashboard-filterbar"]',
    title: "Deine Akten im Blick",
    text: "Alle Akten mit Status und letzter Änderung. Mit Suche und Filtern findest du jede Akte in Sekunden — nach Kennzeichen, Kunde oder Status.",
  },
  {
    target: '[data-testid="nav-kunden"]',
    title: "Kunden",
    text: "Deine Kundenkartei: einmal angelegt, in jeder Akte wiederverwendbar — inklusive Kontaktdaten und Historie.",
  },
  {
    target: '[data-testid="nav-termine"]',
    title: "Termine",
    text: "Besichtigungstermine planen und mit Akten verknüpfen — inklusive Kartenansicht für die Route.",
  },
  {
    target: '[data-testid="nav-aufgaben"]',
    title: "Aufgaben",
    text: "To-dos pro Akte oder fürs Team anlegen und zuweisen — fällige Aufgaben erscheinen im Dashboard.",
  },
  {
    target: '[data-testid="nav-billing"]',
    title: "Dein Abo",
    text: "Hier siehst du deinen Tarif (Flexibel: 85 €/Monat + 75 €/Gutachten · Flatrate: 850 €/Monat + 17 €/Gutachten, zzgl. USt.) und verwaltest dein SEPA-Mandat.",
  },
  {
    target: '[data-testid="nav-buchhaltung"]',
    title: "Buchhaltung",
    text: "Rechnungen kontrollieren und mit einem Klick senden — der Betrag wird sofort per Stripe-SEPA eingezogen, den Status siehst du live.",
  },
  {
    target: '[data-testid="nav-shop"]',
    title: "Shop & Werbung",
    text: "Werbematerial, Shop — und im Tab „Schulung“ findest du die komplette Schulungs-PowerPoint samt PDF-Handout zum Download.",
  },
  {
    target: '[data-testid="header-user-dropdown"]',
    title: "Dein Profil",
    text: "Stammdaten, Logo, Bankverbindung und Einstellungen — und hier kannst du diese Tour jederzeit neu starten.",
  },
  {
    center: true,
    title: "Fertig — viel Erfolg! 🎉",
    text: "Das war der Überblick. Tipp: Leg gleich eine Test-Akte an und probiere den Ablauf einmal durch. Die ausführliche Schulung (PowerPoint + PDF) liegt unter Shop & Werbung → Schulung.",
  },
];

export function AppTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Nur Schritte anzeigen, deren Ziel-Element existiert (Rolle/Viewport-abhängig)
  const steps = useMemo(() => {
    if (!active) return [];
    return STEPS.filter((s) => {
      if (s.center) return true;
      const el = document.querySelector(s.target);
      return el && el.offsetParent !== null;
    });
  }, [active]);

  const start = useCallback(() => {
    if (location.pathname !== "/dashboard") navigate("/dashboard");
    setTimeout(() => { setIdx(0); setActive(true); }, 400);
  }, [location.pathname, navigate]);

  // Auto-Start beim ersten Login (pro Gerät), nur auf dem Dashboard
  useEffect(() => {
    if (!user || localStorage.getItem(DONE_KEY)) return;
    if (location.pathname !== "/dashboard") return;
    if (user.sepa_mandate_missing) return;
    const t = setTimeout(() => setActive(true), 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, location.pathname]);

  // Manueller Start über Profil-Menü
  useEffect(() => {
    const h = () => start();
    window.addEventListener("eg:start-tour", h);
    return () => window.removeEventListener("eg:start-tour", h);
  }, [start]);

  // Spotlight-Position berechnen
  useEffect(() => {
    if (!active || !steps.length) return;
    const step = steps[Math.min(idx, steps.length - 1)];
    if (step.center) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 });
    };
    const t = setTimeout(update, 350);
    window.addEventListener("resize", update);
    return () => { clearTimeout(t); window.removeEventListener("resize", update); };
  }, [active, idx, steps]);

  if (!active || !steps.length) return null;
  const step = steps[Math.min(idx, steps.length - 1)];
  const isLast = idx >= steps.length - 1;

  const finish = () => {
    localStorage.setItem(DONE_KEY, "1");
    setActive(false);
    setIdx(0);
  };

  // Tooltip-Position: unter dem Ziel, sonst darüber; zentriert bei center-Steps
  let tipStyle = {};
  if (step.center || !rect) {
    tipStyle = { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  } else {
    const below = rect.top + rect.height + 16;
    const useAbove = below > window.innerHeight - 230;
    tipStyle = {
      top: useAbove ? Math.max(16, rect.top - 210) : below,
      left: Math.min(Math.max(16, rect.left), window.innerWidth - 400),
    };
  }

  return (
    <div className="fixed inset-0 z-[100]" data-testid="app-tour">
      {/* Abdunkelung + Spotlight */}
      {rect && !step.center ? (
        <div
          className="absolute rounded-xl pointer-events-none transition-all duration-300"
          style={{
            top: rect.top, left: rect.left, width: rect.width, height: rect.height,
            boxShadow: "0 0 0 9999px rgba(9, 9, 18, 0.62)",
            border: "2px solid #18AFFD",
          }}
        />
      ) : (
        <div className="absolute inset-0 bg-[rgba(9,9,18,0.62)]" />
      )}

      {/* Tooltip-Karte */}
      <div
        className="absolute w-[368px] max-w-[calc(100vw-32px)] bg-white rounded-2xl shadow-2xl border border-zinc-200 p-5"
        style={tipStyle}
        data-testid="app-tour-card"
      >
        <button
          type="button"
          onClick={finish}
          className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700 transition-colors"
          data-testid="app-tour-close"
          title="Tour beenden"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-2 mb-1.5">
          <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gradient-to-br from-sky-500 to-violet-600 text-white">
            <Sparkles className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-600">
            Schritt {idx + 1} von {steps.length}
          </span>
        </div>
        <div className="font-semibold text-zinc-900 text-[16px]" data-testid="app-tour-title">{step.title}</div>
        <div className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{step.text}</div>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1">
            {steps.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-sky-500" : "w-1.5 bg-zinc-200"}`} />
            ))}
          </div>
          <div className="flex gap-2">
            {idx > 0 && (
              <Button variant="outline" size="sm" onClick={() => setIdx((i) => i - 1)} data-testid="app-tour-back">
                <ArrowLeft className="h-3.5 w-3.5" />
              </Button>
            )}
            {!isLast ? (
              <Button size="sm" className="brand-gradient-bg text-white" onClick={() => setIdx((i) => i + 1)} data-testid="app-tour-next">
                Weiter <ArrowRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            ) : (
              <Button size="sm" className="brand-gradient-bg text-white" onClick={finish} data-testid="app-tour-finish">
                Los geht&apos;s!
              </Button>
            )}
          </div>
        </div>
        {idx === 0 && (
          <button
            type="button"
            onClick={finish}
            className="mt-3 text-xs text-zinc-400 hover:text-zinc-600 underline underline-offset-2"
            data-testid="app-tour-skip"
          >
            Tour überspringen
          </button>
        )}
      </div>
    </div>
  );
}
